import React, { useEffect, useState } from "react";
import {
  getAllDeviceConfigs,
  addDeviceConfig,
  updateDeviceConfig,
  deleteDeviceConfig,
} from "../services/api";
import { Server, Search, Plus, Trash2, Edit2, X } from "lucide-react";
import Skeleton from "../Components/SkeletonLoader";
import useAuth from "../useAuth";

const Devices = () => {
  const { user, token } = useAuth();
  const [ipData, setIpData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentIp, setCurrentIp] = useState({
    id: null,
    deviceName: "",
    ip: "",
    subnet: "",
    gateway: "",
    macAddress: "",
    serialNo: "",
    status: "Online",
    lastLogTime: new Date().toISOString(),
    createdAt: "",
    createdBy: 0,
    deleted: false,
  });
  const [originalIp, setOriginalIp] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const fetchData = async () => {
    try {
      setLoading(true);
      const data = await getAllDeviceConfigs(currentPage, pageSize);
      setIpData(data?.data || []);
      setTotalCount(data?.count || 0);
    } catch (err) {
      console.error("Failed to fetch devices:", err);
      setError("Failed to load data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize]);

  const handleCreate = () => {
    setCurrentIp({
      id: null,
      deviceName: "",
      ip: "",
      subnet: "",
      gateway: "",
      macAddress: "",
      serialNo: "",
      status: "Online",
      lastLogTime: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      createdBy: 0,
      deleted: false,
    });
    setIsEditing(false);
    setIsModalOpen(true);
  };

  const handleEdit = (ip) => {
    setCurrentIp(ip);
    setOriginalIp(ip.ip);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleDelete = async (ip) => {
    if (window.confirm("Are you sure you want to delete this device?")) {
      try {
        await deleteDeviceConfig(ip);
        setIpData((prev) => prev.filter((item) => item.ip !== ip));
      } catch (err) {
        console.error("Failed to delete device:", err);
        alert("Failed to delete record.");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing) {
        await updateDeviceConfig(originalIp, currentIp);
      } else {
        await addDeviceConfig(currentIp);
      }
      fetchData(); // Refresh data
      setIsModalOpen(false);
    } catch (err) {
      console.error("Failed to save device:", err);
      const errorMessage =
        err.response?.data?.title ||
        err.response?.data ||
        err.message ||
        "Failed to save record.";
      alert(`Error: ${errorMessage}`);
    }
  };

  const filteredData = ipData.filter(
    (item) =>
      item.ip?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.deviceName?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  if (loading) {
    return (
      <div className="p-4 space-y-4 bg-gray-50 min-h-screen">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-48" />
          <div className="flex gap-2">
            <Skeleton className="h-9 w-64" />
            <Skeleton className="h-9 w-32" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <Skeleton className="h-4 w-full" />
          </div>
          <div className="p-4 space-y-3">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 min-h-screen text-gray-900 font-sans">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
            <Server className="h-6 w-6 text-blue-600" />
            Device Manager
            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-blue-50 text-blue-700 border border-blue-100">
              {totalCount} Total
            </span>
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Manage your network infrastructure and fleet.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search devices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 text-sm rounded-lg border border-gray-200 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none w-full sm:w-64 transition-all shadow-sm"
            />
          </div>
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all shadow-sm active:scale-95"
          >
            <Plus className="h-4 w-4" />
            <span>Add Device</span>
          </button>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm flex flex-col overflow-hidden">
        {error && (
          <div className="p-4 bg-red-50 text-red-700 border-b border-red-100 flex items-center gap-2 text-sm font-medium">
            <X className="h-4 w-4" />
            {error}
          </div>
        )}

        {/* Refined Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/50 border-b border-gray-200 text-xs font-bold text-gray-500 uppercase tracking-wider">
                <th className="px-6 py-4 w-16 text-center">#</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Device Details</th>
                <th className="px-6 py-4">Network Config</th>
                <th className="px-6 py-4">Gateway</th>
                <th className="px-6 py-4">Hardware Info</th>
                <th className="px-6 py-4">Last Seen</th>
                <th className="px-6 py-4 text-right">Settings</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredData.length > 0 ? (
                filteredData.map((item, index) => (
                  <tr
                    key={item.ip}
                    className="group hover:bg-blue-50/30 transition-colors"
                  >
                    <td className="px-6 py-4 text-center text-gray-400 font-mono text-xs font-medium">
                      {(currentPage - 1) * pageSize + index + 1}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${
                          item.status === "Online"
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : "bg-rose-50 text-rose-700 border-rose-200"
                        }`}
                      >
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${item.status === "Online" ? "bg-emerald-500" : "bg-rose-500"}`}
                        />
                        {item.status || "Offline"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="font-bold text-gray-900">
                          {item.deviceName || "Unknown"}
                        </span>
                        <span className="text-xs text-gray-500">
                          Network Switch
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-0.5">
                        <span className="font-mono text-sm font-semibold text-gray-700 bg-gray-100 px-2 py-0.5 rounded w-fit">
                          {item.ip}
                        </span>
                        <span className="text-[10px] text-gray-400 font-medium ml-0.5">
                          Mask: {item.subnet || "-"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600 font-mono text-sm">
                      {item.gateway || "-"}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold text-gray-400 uppercase w-8">
                            MAC
                          </span>
                          <span className="text-xs font-mono text-gray-600">
                            {item.macAddress || "-"}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold text-gray-400 uppercase w-8">
                            S/N
                          </span>
                          <span className="text-xs font-mono text-gray-600">
                            {item.serialNo || "-"}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {item.lastLogTime ? (
                        <div className="flex flex-col text-xs">
                          <span className="font-semibold text-gray-700">
                            {new Date(item.lastLogTime).toLocaleDateString()}
                          </span>
                          <span className="text-gray-400">
                            {new Date(item.lastLogTime).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {!user?.isReadOnly && (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleEdit(item)}
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                            title="Edit Configuration"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(item.ip)}
                            className="p-2 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all"
                            title="Remove Device"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="8"
                    className="px-6 py-16 text-center text-gray-500"
                  >
                    <div className="flex flex-col items-center justify-center gap-3">
                      <div className="p-4 rounded-full bg-gray-50">
                        <Search className="h-8 w-8 text-gray-300" />
                      </div>
                      <div className="space-y-1">
                        <p className="font-semibold text-gray-900">
                          No devices found
                        </p>
                        <p className="text-xs text-gray-400">
                          Adjust your search or add a new device.
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Compact Pagination */}
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between rounded-b-lg">
          <span className="text-xs text-gray-500 font-medium">
            Showing {Math.min((currentPage - 1) * pageSize + 1, totalCount)}-
            {Math.min(currentPage * pageSize, totalCount)} of {totalCount}
          </span>

          <div className="flex items-center gap-3">
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="text-xs border-gray-300 rounded-md py-1 pl-2 pr-6 bg-white focus:ring-blue-500 focus:border-blue-500 outline-none"
            >
              {[10, 20, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size} / page
                </option>
              ))}
            </select>

            <div className="flex items-center gap-1 textno">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="p-1.5 rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
              >
                Prev
              </button>
              <span className="px-2 text-xs font-medium text-gray-700">
                Page {currentPage}
              </span>
              <button
                onClick={() =>
                  setCurrentPage((prev) =>
                    prev * pageSize < totalCount ? prev + 1 : prev,
                  )
                }
                disabled={currentPage * pageSize >= totalCount}
                className="p-1.5 rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Compact White Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md border border-gray-100 overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
              <h3 className="text-lg font-bold text-gray-900">
                {isEditing ? "Edit Device" : "New Device"}
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-1 rounded-full transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="space-y-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase">
                    Device Name
                  </label>
                  <input
                    type="text"
                    required
                    value={currentIp.deviceName}
                    onChange={(e) =>
                      setCurrentIp({ ...currentIp, deviceName: e.target.value })
                    }
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all placeholder:text-gray-400"
                    placeholder="e.g. Switch-Floor-1"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase">
                      IP Address
                    </label>
                    <input
                      type="text"
                      required
                      value={currentIp.ip}
                      onChange={(e) =>
                        setCurrentIp({ ...currentIp, ip: e.target.value })
                      }
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm font-mono focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                      placeholder="192.168.1.1"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase">
                      Subnet
                    </label>
                    <input
                      type="text"
                      value={currentIp.subnet}
                      onChange={(e) =>
                        setCurrentIp({ ...currentIp, subnet: e.target.value })
                      }
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm font-mono focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                      placeholder="255.255.255.0"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase">
                      Gateway
                    </label>
                    <input
                      type="text"
                      value={currentIp.gateway}
                      onChange={(e) =>
                        setCurrentIp({ ...currentIp, gateway: e.target.value })
                      }
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm font-mono focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                      placeholder="192.168.1.254"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase">
                      MAC Address
                    </label>
                    <input
                      type="text"
                      value={currentIp.macAddress}
                      onChange={(e) =>
                        setCurrentIp({
                          ...currentIp,
                          macAddress: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm font-mono focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                      placeholder="AA:BB:CC:DD:EE:FF"
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase">
                    Serial Number
                  </label>
                  <input
                    type="text"
                    value={currentIp.serialNo}
                    onChange={(e) =>
                      setCurrentIp({ ...currentIp, serialNo: e.target.value })
                    }
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm font-mono focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                    placeholder="SN12345678"
                  />
                </div>
              </div>

              <div className="pt-4 flex gap-3">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 text-sm transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 text-sm transition-all shadow-md active:scale-95"
                >
                  Save Device
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Devices;
